/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { ui } from "ui/tokens";
import api from "services/api";

export const columns = [
  { name: "grupo", align: "left" },
  { name: "creado", align: "center" },
  { name: "action", align: "center" },
];

/* ---------------- helpers ---------------- */

const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return `${String(dt.getDate()).padStart(2,"0")}/${String(dt.getMonth()+1).padStart(2,"0")}/${String(dt.getFullYear()).slice(-2)}`;
};

const isHttp = (s = "") => /^https?:\/\//i.test(s);

/** Si es filename => /attachments/<filename>, si ya es URL => la regresa tal cual */
const toCandidateUrls = (v = "", id) => {
  if (!v) return [];
  if (isHttp(v)) return [v]; // URL absoluta (posible presigned)

  const noExt = String(v).replace(/\.[^.]+$/, "");
  const arr = [
    `/attachments/${encodeURIComponent(v)}`,
    `/attachments/${encodeURIComponent(noExt)}`,
  ];

  // rutas “opcionales” que podrías tener en tu backend:
  if (id != null) {
    arr.push(`/groups/${encodeURIComponent(id)}/file/profile_picture`);
  }

  return arr;
};

/* Mini componente que intenta bajar la imagen con token y mostrarla */
function Thumb({ source, alt, size = 36, radius = 8 }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    let toRevoke = "";

    async function load() {
      if (!source) { setUrl(""); setErr(true); return; }

      const candidates = Array.isArray(source) ? source : [source];
      for (const s of candidates) {
        try {
          if (isHttp(s)) {
            // URL absoluta externa (presigned) => úsala directa
            setUrl(s);
            setErr(false);
            return;
          }

          const res = await api.get(s, { responseType: "blob" });
          if (cancel) return;
          if (res?.status === 200 && res?.data) {
            const objectUrl = URL.createObjectURL(res.data);
            toRevoke = objectUrl;
            setUrl(objectUrl);
            setErr(false);
            return;
          }
        } catch {
          // probar siguiente candidato
        }
      }
      // Si ninguno funcionó…
      setErr(true);
      setUrl("");
    }

    setErr(false);
    setUrl("");
    load();

    return () => {
      cancel = true;
      if (toRevoke) URL.revokeObjectURL(toRevoke);
    };
  }, [source]);

  const style = {
    width: size,
    height: size,
    borderRadius: radius,
    objectFit: "cover",
    display: "inline-block",
    background: "#eee",
  };

  if (!url || err) {
    return <div style={style} aria-label={alt} title={alt} />;
  }
  return <img src={url} alt={alt} style={style} />;
}

/* ---------------- tabla ---------------- */

export default function buildGruposTable(items = [], adapter) {
  const rows = (items || []).map((raw, i) => {
    const g = adapter
      ? adapter(raw, i)
      : {
          id: raw.id ?? raw._id ?? i,
          nombre: raw.name ?? raw.nombre ?? `Grupo ${raw.id ?? i}`,
          creado: raw.created_at ?? raw.createdAt,
          onEdit: () => {},
          onDelete: () => {},
        };

    // detectar imagen desde el adaptado o el raw:
    const picture =
      g.picture ||
      raw?.profile_picture_url ||
      raw?.profilePictureUrl ||
      raw?.imageUrl ||
      raw?.profile_picture ||
      raw?.profilePicture ||
      raw?.foto ||
      "";

    // candidatos de descarga (con token). Si picture es URL absoluta presigned, se usa directo.
    const srcCandidates = toCandidateUrls(picture, g.id);

    const searchText = String(g.nombre || "");

    return {
      // 👇 Mostramos miniatura + nombre
      grupo: (
        <SoftBox display="flex" alignItems="center" gap={1.25}>
          <Thumb source={srcCandidates} alt={g.nombre} />
          <SoftTypography variant="button" fontWeight="medium">
            {g.nombre}
          </SoftTypography>
        </SoftBox>
      ),

      creado: (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          {fmt(g.creado)}
        </SoftTypography>
      ),

      action: (
        <SoftBox display="flex" gap={0.5} justifyContent="center">
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => { e.preventDefault(); g.onEdit?.(g); }}
              sx={{
                "& .i": { color: ui.icon?.edit?.base || "#0ea5e9" },
                "&:hover .i": { color: ui.icon?.edit?.hover || "#0284c7" },
              }}
            >
              <Icon className="i" fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={(e) => { e.preventDefault(); g.onDelete?.(g); }}
              sx={{
                "& .i": { color: ui.icon?.del?.base || "#ef4444" },
                "&:hover .i": { color: ui.icon?.del?.hover || "#b91c1c" },
              }}
            >
              <Icon className="i" fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </SoftBox>
      ),

      // 🔍 Campo auxiliar para que el filtro de búsqueda funcione aunque el cell tenga más nodos
      _search: searchText.toLowerCase(),
    };
  });

  return { columns, rows };
}
